import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { AUTH_TYPES } from '@/pages/auth/constants/authConstants';
import { DEV_CREDENTIALS } from '@/config/devCredentials';

/**
 * @fileoverview Auth Flow Store - Centralized state management for authentication flows
 *
 * This store manages temporary authentication state separate from the main user store.
 * It handles both EDS (Electronic Digital Signature) and Admin authentication flows
 * with comprehensive state tracking, validation, and history management.
 *
 * @author Claude Code
 * @since Phase 3 - State Management Refactor
 */

/**
 * @typedef {Object} EDSState
 * @property {Array} certificates - Available EDS certificates
 * @property {number|null} selectedIndex - Index of selected certificate
 * @property {Object|null} selectedCert - Selected certificate object
 * @property {boolean} isSearchingKey - Whether currently searching for keys
 * @property {boolean} isKeyFound - Whether keys were found
 * @property {boolean} hasError - Whether there's an error state
 * @property {boolean} privacyAgreed - Whether user agreed to privacy terms
 * @property {boolean} isAuthenticating - Whether authentication is in progress
 * @property {string|null} keyId - EDS key identifier
 * @property {string} pkcsInfo - PKCS7 signature information
 * @property {string|null} backendGuid - Backend GUID for authentication
 */

/**
 * @typedef {Object} AdminState
 * @property {Object} credentials - Admin login credentials
 * @property {string} credentials.email - Email address
 * @property {string} credentials.password - Password
 * @property {boolean} credentials.showPassword - Whether password is visible
 * @property {Object} errors - Form validation errors
 * @property {boolean} errors.email - Email field error state
 * @property {boolean} errors.password - Password field error state
 * @property {boolean} isLoading - Whether admin login is in progress
 * @property {number|null} lastSubmissionTime - Timestamp of last submission
 */

/**
 * @typedef {Object} AuthAttempt
 * @property {string} type - Type of authentication attempt ('admin' | 'eds')
 * @property {boolean} success - Whether attempt was successful
 * @property {number} timestamp - Timestamp of attempt
 * @property {string} [email] - Email for admin attempts
 * @property {string} [userId] - User ID for successful attempts
 * @property {string} [error] - Error message for failed attempts
 * @property {string} [stage] - Stage where EDS auth failed
 */

/**
 * Auth Flow Store - Manages authentication flow state
 * Separate from user store to handle temporary auth states
 */
export const useAuthFlowStore = create()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Authentication flow state
      authType: AUTH_TYPES.EDS,
      isLoading: false,
      lastAuthAttempt: null,
      authHistory: [],

      // EDS specific state
      edsState: {
        certificates: [],
        selectedIndex: null,
        selectedCert: null,
        isSearchingKey: false,
        isKeyFound: false,
        hasError: false,
        privacyAgreed: false,
        isAuthenticating: false,
        keyId: null,
        pkcsInfo: '',
        backendGuid: null,
      },

      // Admin specific state
      adminState: {
        credentials: {
          email: DEV_CREDENTIALS.admin.email,
          password: DEV_CREDENTIALS.admin.password,
          showPassword: false,
        },
        errors: {
          email: false,
          password: false,
        },
        isLoading: false,
        lastSubmissionTime: null,
      },

      // Client specific state
      clientState: {
        credentials: {
          TIN: DEV_CREDENTIALS.client.TIN,
          contractId: DEV_CREDENTIALS.client.contractId,
          alias: '',
        },
        errors: {
          TIN: false,
          contractId: false,
          alias: false,
        },
        isLoading: false,
        lastSubmissionTime: null,
      },

      // Actions
      setAuthType: type => {
        set(state => ({
          authType: type,
          // Clear previous errors when switching
          adminState: {
            ...state.adminState,
            errors: { email: false, password: false },
          },
          edsState: {
            ...state.edsState,
            hasError: false,
          },
          clientState: {
            ...state.clientState,
            errors: { TIN: false, contractId: false, alias: false },
          },
        }));
      },

      setLoading: isLoading => {
        set({ isLoading });
      },

      // EDS Actions
      setEDSCertificates: certificates => {
        set(state => ({
          edsState: {
            ...state.edsState,
            certificates,
            isKeyFound: certificates.length > 0,
            hasError: false,
          },
        }));
      },

      setEDSSelectedIndex: (index, certificate) => {
        set(state => ({
          edsState: {
            ...state.edsState,
            selectedIndex: index,
            selectedCert: certificate,
          },
        }));
      },

      setEDSSearchingKey: isSearching => {
        set(state => ({
          edsState: {
            ...state.edsState,
            isSearchingKey: isSearching,
            hasError: isSearching ? false : state.edsState.hasError,
          },
        }));
      },

      setEDSError: hasError => {
        set(state => ({
          edsState: {
            ...state.edsState,
            hasError,
            isSearchingKey: false,
          },
        }));
      },

      setEDSPrivacyAgreed: agreed => {
        set(state => ({
          edsState: {
            ...state.edsState,
            privacyAgreed: agreed,
          },
        }));
      },

      setEDSAuthenticating: isAuthenticating => {
        set(state => ({
          edsState: {
            ...state.edsState,
            isAuthenticating,
          },
        }));
      },

      setEDSKeyId: keyId => {
        set(state => ({
          edsState: {
            ...state.edsState,
            keyId,
          },
        }));
      },

      setEDSPkcsInfo: pkcsInfo => {
        set(state => ({
          edsState: {
            ...state.edsState,
            pkcsInfo,
          },
        }));
      },

      setEDSBackendGuid: backendGuid => {
        set(state => ({
          edsState: {
            ...state.edsState,
            backendGuid,
          },
        }));
      },

      resetEDSState: () => {
        set(() => ({
          edsState: {
            certificates: [],
            selectedIndex: null,
            selectedCert: null,
            isSearchingKey: false,
            isKeyFound: false,
            hasError: false,
            privacyAgreed: false,
            isAuthenticating: false,
            keyId: null,
            pkcsInfo: '',
            backendGuid: null,
          },
        }));
      },

      // Admin Actions
      setAdminCredentials: (field, value) => {
        set(state => ({
          adminState: {
            ...state.adminState,
            credentials: {
              ...state.adminState.credentials,
              [field]: value,
            },
            errors: {
              ...state.adminState.errors,
              [field]: false, // Clear error when user types
            },
          },
        }));
      },

      setAdminErrors: errors => {
        set(state => ({
          adminState: {
            ...state.adminState,
            errors: {
              ...state.adminState.errors,
              ...errors,
            },
          },
        }));
      },

      setAdminLoading: isLoading => {
        set(state => ({
          adminState: {
            ...state.adminState,
            isLoading,
            lastSubmissionTime: isLoading
              ? Date.now()
              : state.adminState.lastSubmissionTime,
          },
        }));
      },

      resetAdminState: () => {
        set(() => ({
          adminState: {
            credentials: {
              email: DEV_CREDENTIALS.admin.email,
              password: DEV_CREDENTIALS.admin.password,
              showPassword: false,
            },
            errors: {
              email: false,
              password: false,
            },
            isLoading: false,
            lastSubmissionTime: null,
          },
        }));
      },

      // Client Actions
      setClientCredentials: (field, value) => {
        set(state => ({
          clientState: {
            ...state.clientState,
            credentials: {
              ...state.clientState.credentials,
              [field]: value,
            },
            errors: {
              ...state.clientState.errors,
              [field]: false,
            },
          },
        }));
      },

      setClientErrors: errors => {
        set(state => ({
          clientState: {
            ...state.clientState,
            errors: {
              ...state.clientState.errors,
              ...errors,
            },
          },
        }));
      },

      setClientLoading: isLoading => {
        set(state => ({
          clientState: {
            ...state.clientState,
            isLoading,
            lastSubmissionTime: isLoading
              ? Date.now()
              : state.clientState.lastSubmissionTime,
          },
        }));
      },

      resetClientState: () => {
        set(() => ({
          clientState: {
            credentials: {
              TIN: DEV_CREDENTIALS.client.TIN,
              contractId: DEV_CREDENTIALS.client.contractId,
              alias: '',
            },
            errors: {
              TIN: false,
              contractId: false,
              alias: false,
            },
            isLoading: false,
            lastSubmissionTime: null,
          },
        }));
      },

      // History tracking
      addAuthAttempt: attempt => {
        set(state => ({
          lastAuthAttempt: attempt,
          authHistory: [
            ...state.authHistory.slice(-9), // Keep last 10 attempts
            {
              ...attempt,
              timestamp: Date.now(),
            },
          ],
        }));
      },

      // Complete reset
      resetAllState: () => {
        set({
          authType: AUTH_TYPES.EDS,
          isLoading: false,
          lastAuthAttempt: null,
          authHistory: [],
          edsState: {
            certificates: [],
            selectedIndex: null,
            selectedCert: null,
            isSearchingKey: false,
            isKeyFound: false,
            hasError: false,
            privacyAgreed: false,
            isAuthenticating: false,
            keyId: null,
            pkcsInfo: '',
            backendGuid: null,
          },
          adminState: {
            credentials: {
              email: DEV_CREDENTIALS.admin.email,
              password: DEV_CREDENTIALS.admin.password,
              showPassword: false,
            },
            errors: {
              email: false,
              password: false,
            },
            isLoading: false,
            lastSubmissionTime: null,
          },
          clientState: {
            credentials: {
              TIN: DEV_CREDENTIALS.client.TIN,
              contractId: DEV_CREDENTIALS.client.contractId,
              alias: '',
            },
            errors: {
              TIN: false,
              contractId: false,
              alias: false,
            },
            isLoading: false,
            lastSubmissionTime: null,
          },
        });
      },

      // Computed values
      getIsAnyLoading: () => {
        const state = get();
        return (
          state.isLoading ||
          state.edsState.isAuthenticating ||
          state.adminState.isLoading ||
          state.clientState.isLoading
        );
      },

      getCanSubmitAdmin: () => {
        const state = get();
        const { credentials, isLoading } = state.adminState;
        return Boolean(!isLoading && credentials.email && credentials.password);
      },

      getCanSubmitEDS: () => {
        const state = get();
        const { selectedIndex, privacyAgreed, isAuthenticating } =
          state.edsState;
        return selectedIndex !== null && privacyAgreed && !isAuthenticating;
      },

      getCanSubmitClient: () => {
        const state = get();
        const { credentials, isLoading } = state.clientState;
        const hasIdentifier = credentials.contractId || credentials.alias;
        return Boolean(!isLoading && credentials.TIN && hasIdentifier);
      },
    })),
    {
      name: 'auth-flow-store',
      enabled: import.meta.env.DEV,
    },
  ),
);
